import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import * as requirementGenerationService from '../services/requirementGenerationService';
import * as requirementService from '../services/requirementService';
import * as projectService from '../services/projectService';

interface GeneratedRequirement {
  title: string;
  description: string;
  acceptance_criteria?: string;
  requirement_type: 'functional' | 'non-functional' | 'technical' | 'business' | 'user-story';
  priority: 'critical' | 'high' | 'medium' | 'low';
  category?: string;
  included: boolean;
}

export function AIGeneratedRequirements() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const { token, user } = useAuth();

  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [seedSize, setSeedSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [generatedRequirements, setGeneratedRequirements] = useState<GeneratedRequirement[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState('');
  const [generationSource, setGenerationSource] = useState<'ai' | 'template'>('ai');
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState('');

  // Load project data
  useEffect(() => {
    if (!token || !projectId) return;

    const loadProject = async () => {
      try {
        const projectData = await projectService.getProjectById(projectId, token);
        setProject(projectData.project);
      } catch (error: any) {
        console.error('Error loading project:', error);
        navigate('/projects');
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [projectId, token, navigate]);

  // Helper to get expected requirement count based on seed size
  const getExpectedCount = (size: 'small' | 'medium' | 'large'): number => {
    switch (size) {
      case 'small': return 15;
      case 'medium': return 25;
      case 'large': return 35;
      default: return 25;
    }
  };

  // Calculate current and remaining counts
  const currentCount = project?.ai_generated_requirements_count || 0;
  const remainingCount = 200 - currentCount;
  const expectedCount = getExpectedCount(seedSize);
  const wouldExceedLimit = currentCount + expectedCount > 200;

  // Generate AI requirements
  const handleGenerateRequirements = async () => {
    if (!token || !project) return;

    // Check if generation would exceed limit
    if (wouldExceedLimit) {
      setGenerationError(`Cannot generate ${expectedCount} requirements. You have ${currentCount} AI-generated requirements and can add ${remainingCount} more (limit: 200).`);
      return;
    }

    setIsGenerating(true);
    setGenerationError('');

    try {
      const result = await requirementGenerationService.generateRequirements(
        {
          projectId: projectId,
          projectType: project.project_type_name || project.project_type_id?.toString() || '',
          projectName: project.name,
          projectDescription: project.description || '',
          startDate: project.start_date || undefined,
          endDate: project.target_completion_date || undefined,
          seedingSize: seedSize
        },
        token
      );

      // Mark all as included by default
      const requirementsWithInclude = result.requirements.map((req: any) => ({
        ...req,
        included: true
      }));

      setGeneratedRequirements(requirementsWithInclude);
      setGenerationSource(result.source);
      setCurrentStep(2);
    } catch (error: any) {
      console.error('Error generating requirements:', error);
      setGenerationError(error.response?.data?.error || error.message || 'Failed to generate requirements');
    } finally {
      setIsGenerating(false);
    }
  };

  // Import requirements into project
  const handleImportRequirements = async () => {
    if (!token || !projectId) return;

    const selectedRequirements = generatedRequirements.filter(req => req.included);

    if (selectedRequirements.length === 0) {
      setImportError('Please select at least one requirement to import');
      return;
    }

    setIsImporting(true);
    setImportError('');

    try {
      // Use bulk import endpoint for AI-generated requirements
      const result = await requirementService.importAIRequirements(
        projectId,
        selectedRequirements.map(req => ({
          title: req.title,
          description: req.description,
          acceptance_criteria: req.acceptance_criteria,
          requirement_type: req.requirement_type,
          priority: req.priority,
          hierarchy_type: 'parent' as 'parent' | 'child' // Default to parent for bulk imports
        })),
        token
      );

      console.log('Import successful:', result);

      // Navigate back to project requirements page
      navigate(`/projects/${projectId}/requirements`);
    } catch (error: any) {
      console.error('Error importing requirements:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message || 'Failed to import requirements';
      setImportError(errorMessage);
    } finally {
      setIsImporting(false);
    }
  };

  // Toggle requirement inclusion
  const toggleRequirement = (index: number) => {
    setGeneratedRequirements(prev =>
      prev.map((req, i) => i === index ? { ...req, included: !req.included } : req)
    );
  };

  // Edit requirement field
  const editRequirement = (index: number, field: keyof GeneratedRequirement, value: any) => {
    setGeneratedRequirements(prev =>
      prev.map((req, i) => i === index ? { ...req, [field]: value } : req)
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-gray-500">Loading project...</div>
        </div>
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-red-600">Project not found</div>
        </div>
      </Layout>
    );
  }

  const selectedCount = generatedRequirements.filter(req => req.included).length;

  // Render Step 1: Seed Size Selection
  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Generate Requirements</h2>
        <p className="text-sm text-gray-600 mb-6">
          Choose the size of the requirement set to generate. AI will create personalized requirements based on your project details.
        </p>
      </div>

      {/* Seeding Size Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-3">
          Select Generation Size
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Small */}
          <button
            onClick={() => setSeedSize('small')}
            className={`p-4 border-2 rounded-lg transition-all text-left ${
              seedSize === 'small'
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-gray-300 hover:border-indigo-300'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-base font-semibold text-gray-900">Small</h4>
              {seedSize === 'small' && (
                <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <p className="text-sm text-gray-600">~25 requirements</p>
            <p className="text-xs text-gray-500 mt-1">Quick start for smaller projects</p>
          </button>

          {/* Medium */}
          <button
            onClick={() => setSeedSize('medium')}
            className={`p-4 border-2 rounded-lg transition-all text-left ${
              seedSize === 'medium'
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-gray-300 hover:border-indigo-300'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-base font-semibold text-gray-900">Medium</h4>
              {seedSize === 'medium' && (
                <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <p className="text-sm text-gray-600">~50 requirements</p>
            <p className="text-xs text-gray-500 mt-1">Balanced coverage for most projects</p>
          </button>

          {/* Large */}
          <button
            onClick={() => setSeedSize('large')}
            className={`p-4 border-2 rounded-lg transition-all text-left ${
              seedSize === 'large'
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-gray-300 hover:border-indigo-300'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-base font-semibold text-gray-900">Large</h4>
              {seedSize === 'large' && (
                <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <p className="text-sm text-gray-600">~100 requirements</p>
            <p className="text-xs text-gray-500 mt-1">Comprehensive for complex projects</p>
          </button>
        </div>
      </div>

      {generationError && (
        <div className="p-4 rounded-md bg-red-50 border border-red-200">
          <p className="text-sm text-red-800">{generationError}</p>
        </div>
      )}

      <div className="flex justify-center pt-6">
        <button
          onClick={handleGenerateRequirements}
          className="inline-flex items-center gap-2 px-6 py-3 border border-transparent rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 shadow-md hover:shadow-lg transition-all"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          Generate Requirements
        </button>
      </div>
    </div>
  );

  // Render Step 2: Review and Import
  const renderStep2 = () => (
    <div className="space-y-6">
      {/* Error message */}
      {importError && (
        <div className="p-4 rounded-md bg-red-50 border border-red-200">
          <p className="text-sm text-red-800">{importError}</p>
        </div>
      )}

      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Review Requirements</h2>
        <p className="text-sm text-gray-600 mb-4">
          Review and customize the generated requirements. You can edit, remove, or adjust requirements before importing them into your project.
        </p>

        {/* Source indicator */}
        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
          generationSource === 'ai' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
        }`}>
          {generationSource === 'ai' ? (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              AI Generated
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Template Based
            </>
          )}
        </div>
      </div>

      {/* Requirements List */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">
            Requirements ({selectedCount} selected)
          </h3>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setCurrentStep(1)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-full text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>

            <button
              onClick={handleImportRequirements}
              disabled={selectedCount === 0 || isImporting}
              className={`inline-flex items-center px-6 py-2 border border-transparent rounded-full text-sm font-medium text-white ${
                selectedCount === 0 || isImporting
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700'
              }`}
            >
              {isImporting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Importing...
                </>
              ) : (
                <>
                  Import Requirements
                  <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Requirements table */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="w-12 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    <input
                      type="checkbox"
                      checked={selectedCount === generatedRequirements.length}
                      onChange={(e) => {
                        const allIncluded = e.target.checked;
                        setGeneratedRequirements(reqs =>
                          reqs.map(req => ({ ...req, included: allIncluded }))
                        );
                      }}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {generatedRequirements.map((req, index) => (
                  <tr key={index} className={!req.included ? 'bg-gray-50 opacity-60' : ''}>
                    <td className="px-3 py-4">
                      <input
                        type="checkbox"
                        checked={req.included}
                        onChange={() => toggleRequirement(index)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-3 py-4">
                      <div className="text-sm font-medium text-gray-900">{req.title}</div>
                      <div className="text-sm text-gray-500 line-clamp-2">{req.description}</div>
                    </td>
                    <td className="px-3 py-4">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800 capitalize">
                        {req.requirement_type.replace(/-/g, ' ')}
                      </span>
                    </td>
                    <td className="px-3 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${
                        req.priority === 'critical' ? 'bg-red-100 text-red-800' :
                        req.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                        req.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {req.priority}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-400 rounded-lg shadow-lg mb-6 p-6">
            <button
              onClick={() => navigate(`/projects/${projectId}`)}
              className="text-sm text-blue-100 hover:text-white mb-2 inline-flex items-center transition-colors"
            >
              ← Back to Project
            </button>
            <h1 className="text-3xl font-bold text-white">
              AI Generated Requirements
            </h1>
            <p className="text-blue-100 mt-2">
              Generate requirements using AI and import them into {project.name}
            </p>
          </div>

          {/* Usage Info Banner */}
          {project && (
            <div className={`rounded-lg shadow p-4 mb-6 ${
              remainingCount <= 20 ? 'bg-red-50 border border-red-200' : 'bg-blue-50 border border-blue-200'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <svg className={`w-5 h-5 ${remainingCount <= 20 ? 'text-red-600' : 'text-blue-600'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className={`text-sm font-medium ${remainingCount <= 20 ? 'text-red-900' : 'text-blue-900'}`}>
                      AI Requirements Usage: {currentCount} / 200 used
                    </p>
                    <p className={`text-xs ${remainingCount <= 20 ? 'text-red-700' : 'text-blue-700'}`}>
                      {remainingCount} requirements remaining for this project
                    </p>
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  remainingCount <= 20
                    ? 'bg-red-100 text-red-800'
                    : 'bg-blue-100 text-blue-800'
                }`}>
                  {remainingCount} left
                </div>
              </div>
            </div>
          )}

          {/* Content Card */}
          <div className="bg-white rounded-lg shadow p-8">
            {/* Loading State */}
            {isGenerating && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
                <p className="text-lg font-medium text-gray-900">Generating Requirements...</p>
                <p className="text-sm text-gray-600 mt-2">This may take a few minutes</p>
              </div>
            )}

            {/* Step Content */}
            {!isGenerating && (
              <>
                {currentStep === 1 && renderStep1()}
                {currentStep === 2 && renderStep2()}
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
